import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Mail, Send, Settings, CheckCircle, XCircle, AlertTriangle, 
  Bell, Save, TestTube, RefreshCw, Server
} from 'lucide-react';

export default function EmailSettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [config, setConfig] = useState({
    gmail_email: '',
    gmail_password: '',
    hostinger_email: '',
    hostinger_password: '',
    hostinger_smtp: 'smtp.hostinger.com',
    notifications_enabled: false
  });

  const [testEmail, setTestEmail] = useState('');
  const [useHostingerTest, setUseHostingerTest] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/notifications/email-config');
      setConfig(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/notifications/email-config', config);
      setTestResult({ success: true, message: 'Configurazione salvata con successo!' });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Errore nel salvataggio' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/notifications/test-email', { 
        to_email: testEmail,
        use_hostinger: useHostingerTest 
      });
      setTestResult({ success: true, message: res.data.message });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Errore nell\'invio' });
    } finally {
      setTesting(false);
    }
  };

  const handleSendExpiryAlerts = async () => {
    try {
      const res = await api.post('/notifications/send-expiry-alerts');
      setTestResult({ success: true, message: res.data.message });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Errore' });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="email-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Mail size={24} className="text-blue-600" />
          Configurazione Email
        </h1>
        <p className="text-sm text-slate-500">Imposta le credenziali per l'invio di notifiche via email</p>
      </div>

      {/* Alert Result */}
      {testResult && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          {testResult.success ? <CheckCircle className="text-green-600" size={20} /> : <XCircle className="text-red-600" size={20} />}
          <span className={testResult.success ? 'text-green-700' : 'text-red-700'}>{testResult.message}</span>
        </div>
      )}

      {/* Main Config Card */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings size={18} className="text-slate-600" />
                Impostazioni SMTP
              </CardTitle>
              <CardDescription>Configura i server email per l'invio delle notifiche</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="notifications-enabled" className="text-sm">Notifiche Attive</Label>
              <Switch
                id="notifications-enabled"
                checked={config.notifications_enabled}
                onCheckedChange={(v) => setConfig({ ...config, notifications_enabled: v })}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="gmail" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="gmail">Gmail (Admin/Capo Cantiere)</TabsTrigger>
              <TabsTrigger value="hostinger">Hostinger (QHSE)</TabsTrigger>
            </TabsList>
            
            {/* Gmail Tab */}
            <TabsContent value="gmail" className="space-y-4 mt-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                <p className="font-medium text-blue-800 mb-2">Come ottenere la Password App Gmail:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Vai su <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/apppasswords</a></li>
                  <li>Accedi con il tuo account Gmail</li>
                  <li>Seleziona "Posta" e "Altro (Gestionale)"</li>
                  <li>Copia la password di 16 caratteri generata</li>
                </ol>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email Gmail</Label>
                  <Input
                    type="email"
                    value={config.gmail_email}
                    onChange={(e) => setConfig({ ...config, gmail_email: e.target.value })}
                    placeholder="admin@gmail.com"
                    data-testid="gmail-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password App Gmail</Label>
                  <Input
                    type="password"
                    value={config.gmail_password}
                    onChange={(e) => setConfig({ ...config, gmail_password: e.target.value })}
                    placeholder="xxxx xxxx xxxx xxxx"
                    data-testid="gmail-password-input"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2 pt-2">
                <Badge variant="outline" className="bg-slate-50">SMTP: smtp.gmail.com</Badge>
                <Badge variant="outline" className="bg-slate-50">Porta: 587 (TLS)</Badge>
              </div>
            </TabsContent>
            
            {/* Hostinger Tab */}
            <TabsContent value="hostinger" className="space-y-4 mt-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm">
                <p className="font-medium text-purple-800 mb-2">Configurazione Hostinger Mail:</p>
                <p className="text-purple-700">Usa le credenziali dell'email creata nel pannello Hostinger. Il server SMTP è solitamente <code className="bg-purple-100 px-1 rounded">smtp.hostinger.com</code>.</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email Hostinger</Label>
                  <Input
                    type="email"
                    value={config.hostinger_email}
                    onChange={(e) => setConfig({ ...config, hostinger_email: e.target.value })}
                    placeholder="qhse@metalcoveringsrl.com"
                    data-testid="hostinger-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={config.hostinger_password}
                    onChange={(e) => setConfig({ ...config, hostinger_password: e.target.value })}
                    placeholder="Password email"
                    data-testid="hostinger-password-input"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Server SMTP</Label>
                  <Input
                    value={config.hostinger_smtp}
                    onChange={(e) => setConfig({ ...config, hostinger_smtp: e.target.value })}
                    placeholder="smtp.hostinger.com"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2 pt-2">
                <Badge variant="outline" className="bg-slate-50">Porta: 465 (SSL)</Badge>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end pt-6 border-t mt-6">
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-email-config-btn">
              {saving ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Salva Configurazione
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test Email Card */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TestTube size={18} className="text-green-600" />
            Test Invio Email
          </CardTitle>
          <CardDescription>Verifica che la configurazione funzioni correttamente</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] space-y-2">
              <Label>Email Destinatario</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                data-testid="test-email-input"
              />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  id="use-hostinger"
                  checked={useHostingerTest}
                  onCheckedChange={setUseHostingerTest}
                />
                <Label htmlFor="use-hostinger" className="text-sm">Usa Hostinger</Label>
              </div>
              <Button onClick={handleTestEmail} disabled={testing || !testEmail} variant="outline" data-testid="send-test-email-btn">
                {testing ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
                Invia Test
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manual Notifications Card */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell size={18} className="text-amber-600" />
            Invio Manuale Notifiche
          </CardTitle>
          <CardDescription>Invia manualmente le notifiche a tutti gli utenti</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button onClick={handleSendExpiryAlerts} variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50">
              <AlertTriangle size={14} className="mr-2" />
              Invia Notifiche Scadenze
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Le notifiche verranno inviate a tutti gli utenti attivi. Gmail per Admin/Capo Cantiere, Hostinger per QHSE.
          </p>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-100 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Server size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Come funzionano le notifiche:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Le email vengono inviate automaticamente quando ci sono scadenze imminenti</li>
                <li>Admin e Capo Cantiere ricevono via Gmail</li>
                <li>QHSE riceve via Hostinger Mail</li>
                <li>Puoi abilitare/disabilitare le notifiche con lo switch in alto</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
